import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2, KeyRound, Eye, EyeOff } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Eyebrow } from '@/components/ui/eyebrow';
import { api } from '@/lib/api';

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function ChangePasswordCard() {
  const [show, setShow] = useState({ cur: false, nw: false, cf: false });
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const newPwd = watch('newPassword');

  async function onSubmit(values: FormValues) {
    if (values.newPassword !== values.confirmPassword) {
      toast.error('Confirmação não confere com a nova senha.');
      return;
    }
    try {
      await api.post('/v1/auth/change-password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success('Senha alterada com sucesso.');
      reset();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha ao alterar senha.';
      toast.error(msg);
    }
  }

  return (
    <Card variant="default" className="p-6">
      <div className="flex items-start gap-3 mb-5">
        <div className="grid h-10 w-10 place-items-center rounded-2 bg-mint-soft text-mint-deep shrink-0">
          <KeyRound className="h-5 w-5" strokeWidth={1.8} />
        </div>
        <div>
          <Eyebrow>Segurança</Eyebrow>
          <h3 className="font-display text-[18px] font-semibold tracking-[-0.015em] text-fg mt-0.5">
            Trocar senha
          </h3>
          <p className="text-sm text-fg-muted mt-1">
            Mínimo 6 caracteres. Sem outras regras por enquanto.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-md">
        <div>
          <Label htmlFor="currentPassword" className="mb-1.5 block">Senha atual</Label>
          <div className="relative">
            <Input
              id="currentPassword"
              type={show.cur ? 'text' : 'password'}
              autoComplete="current-password"
              {...register('currentPassword', { required: 'Obrigatório' })}
              className="pr-9"
            />
            <button
              type="button"
              onClick={() => setShow((s) => ({ ...s, cur: !s.cur }))}
              className="absolute right-2 top-1/2 -translate-y-1/2 grid h-7 w-7 place-items-center rounded-1 text-fg-muted hover:text-fg"
              aria-label={show.cur ? 'Ocultar' : 'Mostrar'}
            >
              {show.cur ? <EyeOff className="h-4 w-4" strokeWidth={1.8} /> : <Eye className="h-4 w-4" strokeWidth={1.8} />}
            </button>
          </div>
          {errors.currentPassword && (
            <p className="text-[11px] text-warn mt-1">{errors.currentPassword.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="newPassword" className="mb-1.5 block">Nova senha</Label>
          <div className="relative">
            <Input
              id="newPassword"
              type={show.nw ? 'text' : 'password'}
              autoComplete="new-password"
              {...register('newPassword', {
                required: 'Obrigatório',
                minLength: { value: 6, message: 'Mínimo 6 caracteres' },
              })}
              className="pr-9"
            />
            <button
              type="button"
              onClick={() => setShow((s) => ({ ...s, nw: !s.nw }))}
              className="absolute right-2 top-1/2 -translate-y-1/2 grid h-7 w-7 place-items-center rounded-1 text-fg-muted hover:text-fg"
              aria-label={show.nw ? 'Ocultar' : 'Mostrar'}
            >
              {show.nw ? <EyeOff className="h-4 w-4" strokeWidth={1.8} /> : <Eye className="h-4 w-4" strokeWidth={1.8} />}
            </button>
          </div>
          {errors.newPassword && (
            <p className="text-[11px] text-warn mt-1">{errors.newPassword.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="confirmPassword" className="mb-1.5 block">Confirmar nova senha</Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={show.cf ? 'text' : 'password'}
              autoComplete="new-password"
              {...register('confirmPassword', {
                required: 'Obrigatório',
                validate: (v) => v === newPwd || 'Não confere com a nova senha',
              })}
              className="pr-9"
            />
            <button
              type="button"
              onClick={() => setShow((s) => ({ ...s, cf: !s.cf }))}
              className="absolute right-2 top-1/2 -translate-y-1/2 grid h-7 w-7 place-items-center rounded-1 text-fg-muted hover:text-fg"
              aria-label={show.cf ? 'Ocultar' : 'Mostrar'}
            >
              {show.cf ? <EyeOff className="h-4 w-4" strokeWidth={1.8} /> : <Eye className="h-4 w-4" strokeWidth={1.8} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-[11px] text-warn mt-1">{errors.confirmPassword.message}</p>
          )}
        </div>

        <div className="pt-2">
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                Alterando…
              </>
            ) : (
              'Alterar senha'
            )}
          </Button>
        </div>
      </form>
    </Card>
  );
}
